import type React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

import {
  companyHasIntegrationType,
  isAdvancedAccountingAvailableForUser,
  isVisibleForAccountantOrAdmin,
  userHasEditorPermission,
} from '@helpers'

import { AccountingButtonVisibilityControl, FeatureDeniedForUser } from '@oldComponents/VisibilityControls'

import { CompanyIntegrationProviders, TmsUserFeaturePermissions, TmsUserPagePermissions } from '@constants'

import { permissionDeniedForUser } from '@permissions'

const WrapperDiv = styled.div`
  /* NOTE: due to Safari issues with flexbox and absolute positioning, this component can't use flexbox (2022.08.23) */
  /* it needs to be used with first-child due to tooltips being rendered after each div, which would break last-child */
  & > *:not(:first-child) {
    margin-left: 5px;
  }
`

interface DashboardActionsSorterProps {
  accounting?: React.ReactNode
  accountingExport?: React.ReactNode
  approve?: React.ReactNode
  attachDokuments?: React.ReactNode
  categorization?: React.ReactNode
  className?: string
  deleting?: React.ReactNode
  download?: React.ReactNode
  exporting?: React.ReactNode
  expressPayment?: React.ReactNode
  filing?: React.ReactNode
  hasIntegrationSzamlazzhu: boolean
  isAccountingExportVisibleForUser: boolean
  isAdvancedAccounting: boolean
  isCashflowVisibleForUser: boolean
  isEditorUser: boolean
  manualPayment?: React.ReactNode
  syncingSzamlazzhu?: React.ReactNode
  tagging?: React.ReactNode
}

function PureDashboardActionsSorter({
  accounting,
  accountingExport,
  approve,
  attachDokuments,
  categorization,
  className,
  deleting,
  download,
  exporting,
  expressPayment,
  filing,
  hasIntegrationSzamlazzhu,
  isAccountingExportVisibleForUser,
  isAdvancedAccounting,
  isCashflowVisibleForUser,
  isEditorUser,
  manualPayment,
  syncingSzamlazzhu,
  tagging,
}: DashboardActionsSorterProps) {
  return (
    <WrapperDiv className={className}>
      {/* trigger approve status */}
      {approve}
      {isEditorUser && (
        <>
          {/* file invoices */}
          {filing}
          {/* set accounting status of invoices */}
          {accounting && !isAdvancedAccounting && (
            <AccountingButtonVisibilityControl>{accounting}</AccountingButtonVisibilityControl>
          )}
          {/* account image transfer to Számlaverzum */}
          {hasIntegrationSzamlazzhu && syncingSzamlazzhu}
          {/* delete invoices */}
          {deleting && (
            <FeatureDeniedForUser permission={TmsUserFeaturePermissions.denyDeletePermission}>
              {deleting}
            </FeatureDeniedForUser>
          )}
          {/* categorisation of invoices */}
          {categorization}
          {/* tagging of invoices */}
          {tagging && (
            <FeatureDeniedForUser permission={TmsUserFeaturePermissions.denyTagPermission}>
              {tagging}
            </FeatureDeniedForUser>
          )}
        </>
      )}

      {/* everybody can see these */}
      {/* download invoice images */}
      {download}
      {/* dokuments bulk ai recognition */}
      {isEditorUser}
      {/* export invoice data */}
      {exporting}

      {isAdvancedAccounting && isAccountingExportVisibleForUser && accountingExport}

      {isEditorUser && (
        <>
          {/* create payment package */}
          {isCashflowVisibleForUser && manualPayment}
          {/* create express payment package */}
          {isCashflowVisibleForUser && expressPayment}
          {/* attach dokuments */}
          {attachDokuments}
        </>
      )}
    </WrapperDiv>
  )
}

export const DashboardActionsSorter = connect((state: Store) => ({
  hasIntegrationSzamlazzhu: companyHasIntegrationType(state, CompanyIntegrationProviders.szamlazz),
  isAccountingExportVisibleForUser: isVisibleForAccountantOrAdmin(state.auth.company.data),
  isAdvancedAccounting: isAdvancedAccountingAvailableForUser(state),
  isCashflowVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyCashflowPermission),
  isEditorUser: userHasEditorPermission(state.auth.company.data.role),
}))(PureDashboardActionsSorter)

DashboardActionsSorter.displayName = 'DashboardActionsSorter'
